import type { VoidComponent } from "solid-js";
import { createEffect, createSignal } from "solid-js";
import { useTranslation } from "../../../state/i18n";
import { useCurrentModal, useModalManager } from "../../../components/modal/ModalManager";
import { createForm } from "../../../components/form/createForm";
import { CUSTOMER_DELETION_DELAY_DAYS } from "../../../constants";
import GenericFailureModal from "../../GenericFailureModal";
import { useCustomerState } from "../../../state/customer";
import { TextButton } from "../../../components/button/Button";
import ApiService from "../../../services/ApiService";
import { Translation } from "../../../utils/Translation";
import DetailModal from "../../../components/modal/DetailModal";
import { Checkbox } from "../../../components/checkbox/Checkbox";
import type { IconName } from "../../../components/icon/Icon";
import { Icon } from "../../../components/icon/Icon";
import { keyFromSelector } from "i18next";
import { Form } from "../../../components/form/Form";
import TerminationFeedbackFields, {
  type TerminationFeedback,
  terminationFeedbackSchema,
} from "./components/TerminationFeedbackFields";

const IconContainer: VoidComponent<{ icon: IconName }> = (props) => (
  <div class="flex size-9 shrink-0 items-center justify-center rounded-sm border border-status-subtle-danger bg-surface-status-subtle-danger icon-status-danger">
    <Icon type="outline" size="xl" name={props.icon} />
  </div>
);

type CustomerDeletionModalProps = {
  deleteOn: Date;
};
const CustomerDeletionModal: VoidComponent<CustomerDeletionModalProps> = (props) => {
  const { t } = useTranslation();
  const { replace } = useModalManager();
  const { close } = useCurrentModal();

  const form = createForm<TerminationFeedback>(
    terminationFeedbackSchema,
    ({ terminationReasons, message }) => {
      replace({
        content: () => (
          <DeletionConfirmationStep
            terminationReasons={terminationReasons}
            message={message}
            deleteOn={props.deleteOn}
          />
        ),
      });
    },
    {
      terminationReasons: [],
      message: undefined,
    },
  );

  return (
    <DetailModal
      title={t(($) => $.SETTINGS_COMPANY_DELETION_REASON_MODAL_TITLE)}
      content={() => (
        <Form form={form}>
          {({ updateValue, error }) => <TerminationFeedbackFields updateValue={updateValue} error={error} />}
        </Form>
      )}
      footer={
        <div class="flex justify-end gap-2">
          <TextButton
            variant="secondary"
            size="large"
            text={t(($) => $.SETTINGS_COMPANY_DELETION_REASON_MODAL_ACTIONS_CANCEL)}
            onClick={() => close()}
          />
          <TextButton
            variant="primary"
            size="large"
            text={t(($) => $.SETTINGS_COMPANY_DELETION_REASON_MODAL_ACTIONS_NEXT)}
            disabled={form.submitDisabled()}
            onClick={() => {
              void form.submit();
            }}
          />
        </div>
      }
    />
  );
};

type DeletionConfirmationStepProps = {
  terminationReasons: string[];
  message: string;
  deleteOn: Date;
};

const DeletionConfirmationStep: VoidComponent<DeletionConfirmationStepProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const { close } = useCurrentModal();
  const customer = useCustomerState();
  const scheduleCustomerDeletion = ApiService.scheduleCustomerDeletion();
  const [hasAgreedToTerms, setAgreedToTerms] = createSignal(false);
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const { changeCloseBehaviour } = useCurrentModal();

  createEffect(() => {
    changeCloseBehaviour(isSubmitting() ? "off" : "default");
  });

  return (
    <DetailModal
      title={t(($) => $.SETTINGS_COMPANY_DELETION_CONFIRMATION_MODAL_TITLE)}
      content={() => (
        <div class="flex flex-col gap-4">
          <p class="text-base-semibold text-secondary">
            {t(($) => $.SETTINGS_COMPANY_DELETION_CONFIRMATION_MODAL_DESCRIPTION)}
          </p>

          <div class="flex flex-col gap-6">
            <div class="flex gap-4">
              <IconContainer icon="download" />
              <div>
                <p class="text-medium-semibold text-primary">
                  {t(($) => $.SETTINGS_COMPANY_DELETION_CONFIRMATION_MODAL_EXPORT_TITLE)}
                </p>
                <p class="text-medium-regular text-secondary">
                  {t(($) => $.SETTINGS_COMPANY_DELETION_CONFIRMATION_MODAL_EXPORT_DESCRIPTION, {
                    days: CUSTOMER_DELETION_DELAY_DAYS,
                  })}
                </p>
              </div>
            </div>

            <div class="flex gap-4">
              <IconContainer icon="trash" />
              <div class="min-w-0">
                <p class="text-medium-semibold text-primary">
                  {t(($) => $.SETTINGS_COMPANY_DELETION_CONFIRMATION_MODAL_DATE_TITLE, {
                    deleteOn: props.deleteOn,
                    formatParams: { deleteOn: { dateStyle: "long" } },
                  })}
                </p>
                <p class="text-medium-regular wrap-break-word text-secondary">
                  <Translation
                    key={keyFromSelector(($) => $.SETTINGS_COMPANY_DELETION_CONFIRMATION_MODAL_DATE_DESCRIPTION)}
                    options={{ customerId: customer.customerId, customerName: customer.name }}
                    components={{
                      b: (bProps) => <span class="text-medium-semibold text-primary">{bProps.children}</span>,
                    }}
                  />
                </p>
              </div>
            </div>

            <div class="flex gap-4">
              <IconContainer icon="creditCard" />
              <div>
                <p class="text-medium-semibold text-primary">
                  {t(($) => $.SETTINGS_COMPANY_DELETION_CONFIRMATION_MODAL_POLICY_TITLE)}
                </p>
                <p class="text-medium-regular text-secondary">
                  {t(($) => $.SETTINGS_COMPANY_DELETION_CONFIRMATION_MODAL_POLICY_DESCRIPTION)}
                </p>
              </div>
            </div>
          </div>

          <Checkbox
            label={t(($) => $.SETTINGS_COMPANY_DELETION_CONFIRMATION_MODAL_AGREE_TO_TERMS)}
            required
            checked={hasAgreedToTerms()}
            setChecked={(value) => setAgreedToTerms(value === true)}
          />
        </div>
      )}
      footer={
        <div class="flex justify-end gap-2">
          <TextButton
            variant="secondary"
            size="large"
            disabled={isSubmitting()}
            text={t(($) => $.SETTINGS_COMPANY_DELETION_CONFIRMATION_MODAL_ACTIONS_CANCEL)}
            onClick={() => close()}
          />
          <TextButton
            variant="primary"
            color="destructive"
            size="large"
            disabled={!hasAgreedToTerms()}
            loading={isSubmitting()}
            text={t(($) => $.SETTINGS_COMPANY_DELETION_CONFIRMATION_MODAL_ACTIONS_CONFIRM)}
            onClick={() => {
              setIsSubmitting(true);
              scheduleCustomerDeletion.mutate(
                {
                  terminationReasons: props.terminationReasons,
                  message: props.message,
                },
                {
                  onSuccess: () => {
                    close();
                  },
                  onError: (err) => {
                    open({
                      content: () => <GenericFailureModal error={err} />,
                    });
                  },
                  onSettled: () => {
                    setIsSubmitting(false);
                  },
                },
              );
            }}
          />
        </div>
      }
    />
  );
};

export default CustomerDeletionModal;
